/**
 * What this browser knows about the email it captured — and CRUCIALLY, whether
 * the write ever actually landed.
 *
 * THE BUG THIS EXISTS TO FIX (and it is not the one to-do 12 describes)
 * --------------------------------------------------------------------
 * To-do 12 says "a failed send must not look like a success", and read
 * literally that points at the sheet's UI: flip the confirmation to "couldn't
 * send — try again". Chasing the sheet is the wrong target, for two reasons:
 *
 *   - the sheet auto-closes at ~2.4s (`DEFAULT_CONFIRMATION_MS`) against a
 *     documented 3-8s cold start, so by the time most failures are knowable
 *     there is no sheet left to put an error in; and
 *   - on the click-out path the DOCUMENT IS GONE — the write rides a
 *     `keepalive` fetch precisely so it survives unload, which also means
 *     nothing is left to observe the response.
 *
 * The state that must not lie is the PERSISTENT one. On master,
 * `markEmailCaptured()` fires off the CONFIRMATION TIMER, not off the write
 * result — so a failed send permanently sets the captured flag, silences the
 * ask forever, and renders "You're all set." on the You tab. That is the lie,
 * and it outlives the sheet by weeks. `captureEmailBeacon` never reads the
 * response at all: it does not check `ok` and ignores `klaviyoForwarded`, which
 * the backend does return.
 *
 * So the record is THREE-STATE, not a boolean:
 *
 *   none        — never given us an address.
 *   unconfirmed — she typed one and we tried. We do NOT know it landed. This is
 *                 the honest resting state after any unload, and it is where a
 *                 rejected write lands too.
 *   confirmed   — the callable answered ok. The only state that silences the
 *                 ask and the only one whose copy may claim we emailed her.
 *
 * WHAT `confirmed` DOES AND DOES NOT MEAN. It means the write landed. It does
 * NOT mean an email arrived: `klaviyoForwarded: true` only says Klaviyo
 * ACCEPTED the event. "Couldn't send — try again" is honest; its inverse is
 * not, which is why every copy path here still offers resend rather than
 * declaring success and closing the door.
 *
 * Wraps `emailCaptureState` rather than replacing it, so the existing flag
 * stays the single source of truth for "captured" and the board trigger and the
 * account surfaces cannot drift. The flag is set ONLY on confirm.
 */

import { useEffect, useState } from 'react';

import { isEmailCaptured, markEmailCaptured } from '../lib/emailCaptureState';

export const CAPTURED_EMAIL_KEY = 'thea:capturedEmail';
export const CAPTURE_STATUS_KEY = 'thea:captureStatus';
/** Same event name `emailCaptureState` dispatches, so one listener covers both. */
const EMAIL_CAPTURED_EVENT = 'thea:emailCapturedChange';

export type CaptureStatus = 'none' | 'unconfirmed' | 'confirmed';

export interface CaptureRecord {
  status: CaptureStatus;
  /** In full, never masked — the only place a typo becomes visible. */
  email: string | null;
}

export const EMPTY_CAPTURE_RECORD: CaptureRecord = { status: 'none', email: null };

function readLocal(key: string): string | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — degrade gracefully */
  }
}

function announce(): void {
  try {
    window.dispatchEvent(new Event(EMAIL_CAPTURED_EVENT));
  } catch {
    /* SSR / no window — nothing listening anyway */
  }
}

export function readCaptureRecord(): CaptureRecord {
  const email = readLocal(CAPTURED_EMAIL_KEY);
  // The legacy boolean flag wins when set: a browser that captured before this
  // module existed is genuinely confirmed as far as anything can tell, and
  // demoting it would re-ask people who already said yes. Migration by
  // interpretation rather than by a backfill nobody can run.
  if (isEmailCaptured()) return { status: 'confirmed', email: email || null };
  const raw = readLocal(CAPTURE_STATUS_KEY);
  if (raw === 'unconfirmed' && email) return { status: 'unconfirmed', email };
  return EMPTY_CAPTURE_RECORD;
}

/**
 * She submitted. Records the ADDRESS and an explicitly UNCONFIRMED status.
 *
 * Call this at submit time, not at confirmation time. It is deliberately not
 * enough to silence the ask — see `captureSilencesAsk`.
 */
export function markCaptureAttempted(email: string): void {
  const value = email.trim();
  if (!value) return;
  writeLocal(CAPTURED_EMAIL_KEY, value);
  writeLocal(CAPTURE_STATUS_KEY, 'unconfirmed');
  announce();
}

/**
 * The write came back ok. THE ONLY path that sets the legacy captured flag, and
 * therefore the only path that permanently silences the ask.
 */
export function markCaptureConfirmed(email: string): void {
  const value = email.trim();
  if (value) writeLocal(CAPTURED_EMAIL_KEY, value);
  writeLocal(CAPTURE_STATUS_KEY, 'confirmed');
  markEmailCaptured();
  announce();
}

/**
 * The write failed and we know it. Stays UNCONFIRMED with the address kept, so
 * she can see what was typed and retry — losing the address here would destroy
 * the only evidence of a typo.
 */
export function markCaptureFailed(email: string): void {
  markCaptureAttempted(email);
}

/** Sign-in. The account owns the address now; a local copy is stale-data risk. */
export function clearCaptureRecord(): void {
  try {
    window.localStorage.removeItem(CAPTURED_EMAIL_KEY);
    window.localStorage.removeItem(CAPTURE_STATUS_KEY);
  } catch {
    /* nothing to do */
  }
  announce();
}

/**
 * Does this record permanently silence the capture ask?
 *
 * ONLY `confirmed`. An unconfirmed attempt leaves the ask alive — but the
 * caller is expected to have snoozed it (a day's quiet, same as a no), so this
 * is "she can be asked again later", never "ask her again immediately".
 */
export function captureSilencesAsk(record: CaptureRecord): boolean {
  return record.status === 'confirmed';
}

/** Reactive read — re-renders on a capture in this tab or another. */
export function useCaptureRecord(): CaptureRecord {
  const [record, setRecord] = useState<CaptureRecord>(readCaptureRecord);
  useEffect(() => {
    const update = () => setRecord(readCaptureRecord());
    window.addEventListener(EMAIL_CAPTURED_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(EMAIL_CAPTURED_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return record;
}

/* ── The write itself ─────────────────────────────────────────────────── */

/** What a capture write can tell us. `ok` is the only field that gates trust. */
export interface CaptureWriteResult {
  ok: boolean;
  /** Klaviyo ACCEPTED the event. Not delivery. Never gate copy on this. */
  klaviyoForwarded?: boolean;
}

/**
 * Run a capture write and keep the persisted record honest about the outcome.
 *
 * The contract that matters: a rejection, a non-ok result, OR a page that goes
 * away mid-flight all leave the record UNCONFIRMED. There is no path where a
 * write we did not see succeed produces a confirmed record — which is the whole
 * point, because "we never observed the response" is overwhelmingly the common
 * case on the click-out path and it must not read as success.
 *
 * Returns the result so a still-mounted sheet can show its error; a sheet that
 * has already closed simply never reads it, and the record is still right.
 */
export async function runCaptureWrite(
  email: string,
  write: (email: string) => Promise<CaptureWriteResult>,
): Promise<CaptureWriteResult> {
  const value = email.trim();
  markCaptureAttempted(value);
  try {
    const result = await write(value);
    // SILENCE IS NOT SUCCESS. Only an explicit `ok: true` confirms.
    //
    // This used to read `result === undefined ? true : Boolean(result.ok)` — a
    // writer that resolved without a body counted as a landed write. That is
    // precisely the shape of `captureEmailBeacon`, the fire-and-forget writer
    // this module's docblock criticises for never reading the response, so the
    // most likely production wiring would have re-created the original lie
    // through the module built to prevent it.
    //
    // The type now demands a result, and this check backstops it at runtime for
    // JS callers and for a writer that resolves with a malformed body. A writer
    // that genuinely cannot observe its own outcome must resolve `{ok: false}`
    // and leave the record unconfirmed — which is the truth, not a failure.
    if (result && result.ok === true) {
      markCaptureConfirmed(value);
      return { ok: true, klaviyoForwarded: result.klaviyoForwarded };
    }
    markCaptureFailed(value);
    return { ok: false, klaviyoForwarded: result?.klaviyoForwarded };
  } catch {
    markCaptureFailed(value);
    return { ok: false };
  }
}
